import { useState, useEffect } from 'react';
import { ButtonGroup, icons, ErrorAlert } from '../../generic';
import ModalSlidePane from '../../modals/ModalSlidePane';
import Button from '../../generic/Button';
import Loader from '../../generic/Loader';
import { useOnConfirm } from '../../../contexts/DialogContext';
import styles from './LicenseTextModal.module.less';
import { userHasPermissionForPolicyType } from '../../../helpers/permissions';
import { Action } from '../../../shared/permissions';
import type { LicensingPolicy } from '../../../../typings/api';
import type { License } from '../../../types';
import { getLicense } from '../../../api/licenses';
import LicenseDetail from '../../licenses/LicenseDetail';

// Custom hook for license details
const useLicenseDetails = (licenseId: string | null) => {
  const [licenseText, setLicenseText] = useState<string | null>(null);
  const [isLicenseLoading, setIsLicenseLoading] = useState(false);
  const [license, setLicense] = useState<License | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!licenseId) {
      return;
    }

    const fetchLicenseDetails = async () => {
      setIsLicenseLoading(true);
      setError(null);

      try {
        const fetchedLicense = await getLicense(licenseId);
        setLicense(fetchedLicense ?? null);
        setLicenseText(fetchedLicense?.licenseText || 'No License Text Available For This License.');
        setIsLicenseLoading(false);
      } catch (err) {
        setError('Failed to load license');
        setIsLicenseLoading(false);
        setLicense(null);
      }
    };

    fetchLicenseDetails();
  }, [licenseId]);

  return { licenseText, isLicenseLoading, license, error };
};

// License Details Section Component
type LicenseDetailsSectionProps = {
  isLoading: boolean;
  error: string | null;
  license: License | null;
};

const LicenseDetailsSection = ({ isLoading, error, license }: LicenseDetailsSectionProps) => {
  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorAlert status={{ error }} />;
  }

  if (!license) {
    return <p><i>No license details available</i></p>;
  }

  return <LicenseDetail key={license.licenseId} license={license} />;
};

// License Text Section Component
type LicenseTextSectionProps = {
  isLoading: boolean;
  error: string | null;
  licenseText: string | null;
};

const LicenseTextSection = ({ isLoading, error, licenseText }: LicenseTextSectionProps) => {
  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <ErrorAlert status={{ error }} />;
  }

  return <pre className="wrap-pre">{licenseText}</pre>;
};

// Main Modal Props
type LicenseTextModalProps = {
  policy: LicensingPolicy;
  licenseId: string | null;
  ruleId: number | null;
  onClose: () => void;
  onAdd: (licenseId: string) => void;
  onEdit: (ruleId: number) => void;
  onDelete: (ruleId: number) => void;
};

// Main Modal Component
const LicenseTextModal = ({ policy, licenseId, ruleId, onClose, onAdd, onEdit, onDelete }: LicenseTextModalProps) => {
  const { licenseText, isLicenseLoading, license, error } = useLicenseDetails(licenseId);
  const onConfirm = useOnConfirm();

  // If the licenseId is not provided, don't render the modal
  if (!licenseId) {
    return null;
  }

  const handleDelete = () =>
    onConfirm({
      title: 'Delete Rule',
      body: `Are you sure you want to delete ${licenseId}?`,
      onConfirm: () => ruleId && onDelete(ruleId),
      confirmText: 'Delete',
      confirmBtnType: 'error',
    });

  return (
    <ModalSlidePane onClose={onClose} width={66} show>
      <div className="container-fluid">
        <div>
          <h2>{licenseId}</h2>
          <div>
            <LicenseDetailsSection
              isLoading={isLicenseLoading}
              error={error}
              license={license}
            />
          </div>
        </div>
        <ButtonGroup>
          <Button
            size="large"
            type="secondary"
            icon={icons.edit}
            isDisabled={!userHasPermissionForPolicyType(Action.Edit, policy.type)}
            onClick={() => ruleId ? onEdit(ruleId) : onAdd(licenseId)}>
            {ruleId ? 'Edit Rule' : 'Add Rule'}
          </Button>
          <Button
            size="large"
            type="error"
            icon={icons.trashRegular}
            isDisabled={ruleId == null || !userHasPermissionForPolicyType(Action.Edit, policy.type)}
            onClick={handleDelete}>
            Delete Rule
          </Button>
        </ButtonGroup>
        <br />
      </div>
      <div className={styles.subnav}>
        <div className={styles.subnavContent}>
          <div className={styles.link}>
            License Text
          </div>
        </div>
      </div>
      <div className={styles.content}>
        <LicenseTextSection
          isLoading={isLicenseLoading}
          error={error}
          licenseText={licenseText}
        />
      </div>
    </ModalSlidePane>
  );
};

export default LicenseTextModal;
